import { useEffect, useRef } from "react";

import { ArrowRight } from "lucide-react";
import { Link } from "@remix-run/react";

import { PaywallContactDialog } from "~/components/billing/paywall-contact-dialog";
import { Button } from "~/components/ui/button";
import type { BillingBlockedPayload } from "~/features/billing/types";
import { trackEvent } from "~/lib/analytics";
import { cn } from "~/lib/utils";

export type BillingSoftGateVariant =
    | "sessionPipeline"
    | "sessionNotes"
    | "partDetail"
    | "meditation"
    | "soap"
    | "practitionerChat"
    | "addPartChat"
    | "assessmentAndChat";

interface BillingSoftGateCopy {
    title: string;
    body: string;
}

interface BillingSoftGateProps {
    variant: BillingSoftGateVariant;
    billing?: BillingBlockedPayload | null;
    className?: string;
    analyticsSurface?: string;
}

interface BillingSoftGateDisplayProps {
    variant: BillingSoftGateVariant;
    hideUpgradeCta?: boolean;
    className?: string;
    upgradeLinkTo?: string;
    onUpgradeClick?: () => void;
    billingState?: string | null;
}

const COPY_BY_VARIANT: Record<BillingSoftGateVariant, BillingSoftGateCopy> = {
    sessionPipeline: {
        title: "Unlock Full Session Analysis",
        body: "Keep your momentum with complete notes, parts analysis, and clear next steps after each session.",
    },
    sessionNotes: {
        title: "Unlock Your Session Insights",
        body: "We'd love to keep supporting your journey with structured notes you can revisit and build on.",
    },
    partDetail: {
        title: "This Part Has More To Share",
        body: "Unlock deeper guidance to explore this part with more clarity and compassion.",
    },
    meditation: {
        title: "Unlock Guided Meditations",
        body: "Guided audio can help you stay connected to your work between sessions.",
    },
    soap: {
        title: "Unlock SOAP Note Generation",
        body: "Generate structured clinical notes in seconds so you can stay focused on care, not formatting.",
    },
    practitionerChat: {
        title: "Continue Exploring With Your Practitioner",
        body: "Keep the conversation going with AI-supported consultation tailored to your client work.",
    },
    addPartChat: {
        title: "Discover More About Your Parts",
        body: "Unlock guided part-creation support to turn this conversation into clear, actionable insights.",
    },
    assessmentAndChat: {
        title: "Unlock Your Session Reflection",
        body: "Chat with your AI consultant to reflect on this session, explore what came up, and prepare for what's next.",
    },
};

// AIDEV-NOTE: Pure presentation component — no hooks, no analytics. Use BillingSoftGate for the hooked version.
export function BillingSoftGateDisplay({
    variant,
    hideUpgradeCta,
    className,
    upgradeLinkTo,
    onUpgradeClick,
    billingState,
}: BillingSoftGateDisplayProps) {
    const copy = COPY_BY_VARIANT[variant];

    return (
        <div
            role="region"
            aria-label="Billing soft gate"
            className={cn(
                "rounded-xl border border-brand-forest-200 bg-brand-forest-50 p-4",
                className,
            )}
        >
            <div className="space-y-1">
                <p className="text-sm font-semibold text-brand-forest-900">
                    {copy.title}
                </p>
                <p className="text-sm text-brand-forest-700">
                    {hideUpgradeCta
                        ? "Some features are temporarily unavailable — your practitioner has been notified."
                        : copy.body}
                </p>
            </div>

            <div className="mt-3 flex items-center gap-3">
                {!hideUpgradeCta && (
                    <Button asChild size="sm" onClick={onUpgradeClick}>
                        <Link to={upgradeLinkTo ?? "/account/membership"}>
                            Upgrade your plan
                            <ArrowRight className="ml-1.5 size-3.5" />
                        </Link>
                    </Button>
                )}
                <PaywallContactDialog
                    surface="soft_gate"
                    billingState={billingState}
                    variant="forest"
                />
            </div>
        </div>
    );
}

export function BillingSoftGate({
    variant,
    billing,
    className,
    analyticsSurface = "billing_soft_gate",
}: BillingSoftGateProps) {
    const hasTracked = useRef(false);

    // AIDEV-NOTE: metricType=null typically indicates practitioner-owned billing from a client surface; hide direct upsell CTA.
    const hideUpgradeCta = billing?.metricType === null;

    useEffect(() => {
        if (hasTracked.current || !billing) return;
        if (
            billing.thresholdState !== "warning_80" &&
            billing.thresholdState !== "warning_100" &&
            billing.thresholdState !== "blocked"
        ) {
            return;
        }

        hasTracked.current = true;
        trackEvent("billing_blocked_ui_shown", {
            surface: analyticsSurface,
            metricType: billing.metricType,
            thresholdState: billing.thresholdState,
            blockReason: billing.blockReason,
        });
    }, [analyticsSurface, billing]);

    return (
        <BillingSoftGateDisplay
            variant={variant}
            hideUpgradeCta={hideUpgradeCta}
            className={className}
            billingState={billing?.thresholdState}
            onUpgradeClick={() => {
                trackEvent("clicked button", {
                    text: `billing upgrade cta (${analyticsSurface})`,
                });
            }}
        />
    );
}
